import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Alert, CircularProgress, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { StatusChip } from "components/StatusChip";
import {
  buildWasteStreamRegistrationUpdateNotes,
  buildWasteStreamRegistrationUpdateTitle,
} from "encamp-shared/src/submissions/wasteStreamRegistration";
import { useState } from "react";

import { refetchWasteStreamGridQueries } from "./refetchWasteStreamGridQueries";
import { RegisterNewTab } from "./StateRegistrationDialog/RegisterNewTab";
import {
  CREATE_WASTE_STREAM_REGISTRATION_UPDATE_SUBMISSION_MUTATION,
  REGISTRATION_PENDING_UPDATE_QUERY,
} from "./WasteStreams/api";

/** The due-date clock for an update runs from the shipments that caused it. */
const DUE_DATE_DESCRIPTION =
  "Due date is set automatically to the earliest shipment carrying a missing code plus 30 days, or 2 weeks from today if that date has already passed.";

/** Everything the dialog needs about the stream whose registration is behind. */
export interface RegistrationUpdateTarget {
  wasteStreamId: string;
  wasteStreamName: string;
  facilityId: string | null | undefined;
  facilityName: string;
  state: string;
  registration: { id: string; stateRegisteredId?: string | null } | null | undefined;
  /**
   * The grid's date window. The TN and KY line-level exclusions only look at
   * shipments inside it, so the preview and the filing use the window the
   * "Update needed" chip was computed with.
   */
  window: RegistrationUpdateWindow;
}

export type RegistrationUpdateWindow = { startDate?: string; endDate?: string };

type PendingUpdateStream = { id: string; name?: string | null; missingFederalWasteCodes: string[] };

/**
 * The codes the filing will ask for and the streams they come from. A stream
 * shows its own codes only when there are other streams to tell it apart from.
 */
function PendingUpdateSummary({
  codes,
  streams,
  stateRegisteredId,
}: {
  codes: string[];
  streams: PendingUpdateStream[];
  stateRegisteredId?: string | null;
}) {
  return (
    <Alert severity="warning" variant="outlined" icon={false}>
      <Typography variant="subtitle2" gutterBottom>
        Missing federal waste codes
        {stateRegisteredId ? ` · Registration ${stateRegisteredId}` : ""}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        {codes.map((code) => (
          <StatusChip key={code} color="warning" variant="outlined" label={code} />
        ))}
      </Stack>
      {streams.map((stream) => (
        <Typography key={stream.id} variant="caption" color="text.secondary" display="block">
          {stream.name || stream.id}
          {streams.length > 1 ? ` — ${stream.missingFederalWasteCodes.join(", ")}` : ""}
        </Typography>
      ))}
    </Alert>
  );
}

/** The preview's four states, so the dialog body reads as one line. */
function PendingUpdateBody({
  loading,
  failed,
  codes,
  streams,
  stateRegisteredId,
}: {
  loading: boolean;
  failed: boolean;
  codes: string[];
  streams: PendingUpdateStream[];
  stateRegisteredId?: string | null;
}) {
  if (loading) {
    return (
      <Stack direction="row" justifyContent="center" sx={{ py: 2 }}>
        <CircularProgress size={24} aria-label="Loading missing codes" />
      </Stack>
    );
  }
  if (failed) {
    return <Alert severity="error">Couldn't load the codes this update would ask for.</Alert>;
  }
  if (codes.length === 0) {
    return (
      <Alert severity="info">
        The registration already covers every code its linked streams ship, so there is nothing to
        file.
      </Alert>
    );
  }
  return (
    <PendingUpdateSummary codes={codes} streams={streams} stateRegisteredId={stateRegisteredId} />
  );
}

export interface CreateRegistrationUpdateSubmissionDialogProps {
  target: RegistrationUpdateTarget;
  onClose: () => void;
  /**
   * Opens the created submission — each grid does that differently (`window.open`
   * vs. `navigate`). Called synchronously when Create is clicked, before any
   * await here, so a grid that opens a new tab can reserve it (via
   * `openInNewTabFromPromise`) inside the click's gesture rather than after the
   * mutation and refetch below have already resolved. `getSubmissionId`
   * resolves to the created submission's id, or undefined if creation failed.
   */
  onCreated: (getSubmissionId: () => Promise<string | undefined>) => void;
}

/**
 * Confirms the update submission before creating it: the missing codes that
 * justify it, the streams they come from, and the exact title and notes it will
 * carry. Only the confirm creates anything — the "Update needed" chip used to
 * fire the mutation on click, which gave staff no chance to check what was
 * being filed.
 *
 * The codes are the registration's `pendingUpdate`, not the clicked stream's:
 * one filing covers every linked stream, and `pendingUpdate` is the same answer
 * the mutation files from, so the preview is what gets filed.
 *
 * Shares `RegisterNewTab` with the "Register new" tab of
 * `StateRegistrationDialog`: same facility line, same assignee/due-date note,
 * same preview.
 */
export function CreateRegistrationUpdateSubmissionDialog({
  target,
  onClose,
  onCreated,
}: CreateRegistrationUpdateSubmissionDialogProps) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const [createUpdateSubmission] = useMutation(
    CREATE_WASTE_STREAM_REGISTRATION_UPDATE_SUBMISSION_MUTATION
  );
  // Covers the whole handler, including the post-mutation refetch, so the
  // button doesn't re-enable while the flow is still finishing.
  const [creating, setCreating] = useState(false);

  const registrationId = target.registration?.id;
  // Network-only: the preview must match what the mutation reads when it runs.
  const pending = useQuery(REGISTRATION_PENDING_UPDATE_QUERY, {
    variables: { id: registrationId ?? "", ...target.window },
    skip: !registrationId,
    fetchPolicy: "network-only",
  });
  const pendingUpdate = pending.data?.stateWasteStreamRegistration?.pendingUpdate;
  const missingFederalWasteCodes = pendingUpdate?.missingFederalWasteCodes ?? [];

  // The same label the resolver writes into the notes: the state-assigned id
  // when the state has issued one, the registration's own id otherwise.
  const registrationLabel = target.registration?.stateRegisteredId ?? registrationId;
  const notes =
    registrationLabel && missingFederalWasteCodes.length > 0
      ? buildWasteStreamRegistrationUpdateNotes(missingFederalWasteCodes, registrationLabel)
      : undefined;
  const canCreate = !pending.loading && !pending.error && missingFederalWasteCodes.length > 0;

  const handleCreate = async () => {
    setCreating(true);
    const getSubmissionId = async (): Promise<string | undefined> => {
      try {
        const result = await createUpdateSubmission({
          variables: { wasteStreamId: target.wasteStreamId, ...target.window },
        });
        const submissionId =
          result.data?.createWasteStreamRegistrationUpdateSubmission.submissionId;
        if (!submissionId) {
          alerts.error("Failed to create the registration update submission.");
          return undefined;
        }
        await refetchWasteStreamGridQueries(client, target.facilityId);
        onClose();
        return submissionId;
      } catch (e) {
        alerts.error("Failed to create the registration update submission.", e);
        return undefined;
      } finally {
        setCreating(false);
      }
    };
    // Kicked off and handed to the caller before any await here, so a caller
    // opening a new tab can reserve it inside this click's gesture.
    const submissionIdPromise = getSubmissionId();
    onCreated(() => submissionIdPromise);
    await submissionIdPromise;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="Create update submission"
      primaryActionButtonProps={{
        onClick: handleCreate,
        disabled: creating || !canCreate,
        loading: creating,
        saveText: "Create update submission",
        hideStartIcon: true,
      }}
    >
      <Stack spacing={2}>
        <PendingUpdateBody
          loading={pending.loading}
          failed={!!pending.error}
          codes={missingFederalWasteCodes}
          streams={pendingUpdate?.wasteStreams ?? []}
          stateRegisteredId={target.registration?.stateRegisteredId}
        />

        <RegisterNewTab
          facilityName={target.facilityName}
          state={target.state}
          title={buildWasteStreamRegistrationUpdateTitle(target.state, target.wasteStreamName)}
          notes={notes}
          dueDateDescription={DUE_DATE_DESCRIPTION}
        />
      </Stack>
    </Dialog>
  );
}
